import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  IconButton,
  Checkbox,
  Radio,
  Tooltip,
  Button
} from '@mui/material';
import {
  Add, 
  Search, 
  Fullscreen, 
  FullscreenExit, 
  SwapVert,
  PersonOutline,
  AssignmentOutlined,
  InboxOutlined,
  InfoOutlined
} from '@mui/icons-material';
import AssigneeAvatarStack from '../../../shared/AssigneeAvatarStack';
import OverflowTooltip from '../../../shared/OverflowTooltip';
import { getAssigneeColor, getTaskTypeColor, getStatusColor, hexToRgba, getPriorityColor } from '../../dashboard/calendar-view/calendarColorUtils';
import { COLORS } from '../../../shared/colors';
import { ENDPOINTS } from '../../../services/endpoints';

const DRAG_HELP_MESSAGE = 'Drag to move these tickets between Backlog and Sprint.';
const LOCAL_CHECKBOX_SX = {
  p: 0,
  color: '#E4E4E4',
  '&.Mui-checked': {
    color: COLORS.brand.primaryDark
  },
  '&.MuiCheckbox-indeterminate': {
    color: COLORS.brand.primaryDark
  }
};


const BacklogTable = (props) => {
  const {

  currentMainView,
  currentMainSprint,
  isDragOverSprint,
  handleDragOver,
  handleDragLeave,
  handleDropOnTarget,
  setIsAddTaskOpen,
  searchTerm,
  setSearchTerm,
  handleSortClick,
  sortOption,
  isExpandedView,
  setIsExpandedView,
  handleScroll,
  isLoading,
  isLoadingMore,
  filteredBacklogTickets,
  visibleBacklogTickets,
  selectedTicketIds,
  handleSelectAllVisible,
  handleSelectTicket,
  isAuthorized,
  handleDragStart,
  checkboxProps,
  formatDateRange,
  filteredMainSprintTickets,
  visibleSprintTickets,
  isMyTicketsDataView,
  selectedCardType
} = props;
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleAvatarClick = (assignee) => {
    let name = '';
    if (typeof assignee === 'string') {
      name = assignee;
    } else if (assignee) {
      name = assignee.name || `${assignee.first_name || ''} ${assignee.last_name || ''}`.trim();
    }
    
    if (name) {
      navigate(`/user-management?search=${encodeURIComponent(name)}`);
    } else {
      navigate(`/user-management`);
    }
  };

  return (
    <div 
      className={`backlog-table-container drag-target-zone ${isDragOverSprint ? 'drag-over' : ''}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={(e) => handleDropOnTarget(e, currentMainView === 'backlog' ? 'backlog' : currentMainSprint?.id)}
    >
      {currentMainView === 'backlog' ? (
        <>
          {/* Backlog View Header */}
          <div className="backlog-table-header">
            <div className="backlog-title-container">
              <Box 
                component="img" 
                src="/icons/backlog.svg" 
                alt="Backlog" 
                sx={{ width: 28, height: 28, mr: 1, flexShrink: 0 }} 
              />
              <span className="backlog-title-main">Backlog</span>
              <Tooltip
                title={DRAG_HELP_MESSAGE}
                arrow
                placement="top"
                slotProps={{ tooltip: { className: 'app-tooltip' } }}
              >
                <IconButton
                  size="small"
                  className="backlog-info-btn"
                  aria-label="Drag and drop help"
                  sx={{ ml: 1, p: 0.25, color: COLORS.slate[400], '&:hover': { color: COLORS.slate[600] } }}
                >
                  <InfoOutlined sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            </div>

            <div className="backlog-table-controls">
              <Tooltip title={t('dashboard.createTask', 'Create Task')} arrow placement="top" slotProps={{ tooltip: { className: 'app-tooltip' } }}>
                <IconButton 
                  size="small" 
                  onClick={() => setIsAddTaskOpen(true)}
                  className="backlog-add-btn"
                  aria-label={t('dashboard.createTask', 'Create Task')}
                  sx={{ 
                    mr: 1, 
                    p: 0.5, 
                    color: COLORS.brand.accent, 
                    bgcolor: COLORS.blue[50],
                    border: `1px solid ${COLORS.blue[200] || '#bae6fd'}`,
                    borderRadius: '6px',
                    '&:hover': { 
                      color: COLORS.brand.accentHover,
                      bgcolor: COLORS.blue[100] || '#e0f2fe',
                      borderColor: COLORS.brand.accentHover
                    },
                    transition: 'all 0.2s ease-in-out'
                  }}
                >
                  <Add sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>

              <div className="backlog-search-wrapper">
                <Search className="search-icon" />
                <input 
                  type="text" 
                  placeholder="Search" 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <button 
                className={`backlog-sort-btn ${sortOption !== 'DEFAULT' ? 'active' : ''}`}
                onClick={handleSortClick}
                style={sortOption !== 'DEFAULT' ? { backgroundColor: COLORS.blue[50], color: COLORS.blue[600], borderColor: COLORS.blue[200] } : {}}
              >
                <SwapVert sx={{ fontSize: 18 }} className="sort-icon" />
                <span>
                  {sortOption === 'PRIORITY_HIGH_TO_LOW' ? 'Priority' : 
                   sortOption === 'SP_LOW_TO_HIGH' ? 'Story Points' : 'Sort'}
                </span>
              </button>

              <IconButton 
                size="small" 
                onClick={() => setIsExpandedView(!isExpandedView)}
                className="backlog-expand-btn"
              >
                {isExpandedView ? <FullscreenExit sx={{ fontSize: 18 }} /> : <Fullscreen sx={{ fontSize: 18 }} />}
              </IconButton>
            </div>
          </div>

          {/* Backlog list */}
          <div 
            className="infinite-scroll-container"
            onScroll={(e) => handleScroll(e, 'backlog')}
          >
            {isLoading ? (
              <Box sx={{ p: 3 }}>
                {[1, 2, 3].map(i => (
                  <Box key={i} className="skeleton-box" sx={{ height: 48, mb: 1, width: '100%' }} />
                ))}
              </Box>
            ) : filteredBacklogTickets.length === 0 ? (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center', 
                p: 6, 
                minHeight: '300px',
                height: '100%',
                flex: 1
              }}>
                <InboxOutlined sx={{ fontSize: 48, color: COLORS.slate[300], mb: 2 }} />
                <Typography variant="h6" color={COLORS.slate[700]} fontWeight={600} gutterBottom>
                  No backlog items remaining
                </Typography>
                <Typography variant="body2" color={COLORS.slate[500]} align="center" maxWidth={400}>
                  Your backlog is completely empty. Create a new ticket to start filling it up.
                </Typography>
                <Button 
                  variant="contained" 
                  startIcon={<Add />} 
                  onClick={() => setIsAddTaskOpen(true)}
                  sx={{ mt: 3, borderRadius: '8px', textTransform: 'none', bgcolor: COLORS.brand.accent, color: COLORS.white, '&:hover': { bgcolor: COLORS.brand.accentHover } }}
                >
                  Create Ticket
                </Button>
              </Box>
            ) : (
              <table className="backlog-table">
                <thead>
                  <tr>
                    {isAuthorized && (
                      <th>
                        <Checkbox 
                          size="small"
                          checked={visibleBacklogTickets.length > 0 && visibleBacklogTickets.every(t => selectedTicketIds.includes(t.id))}
                          indeterminate={visibleBacklogTickets.some(t => selectedTicketIds.includes(t.id)) && !visibleBacklogTickets.every(t => selectedTicketIds.includes(t.id))}
                          onChange={() => handleSelectAllVisible(visibleBacklogTickets)}
                          sx={LOCAL_CHECKBOX_SX}
                        />
                      </th>
                    )}
                    <th>TICKET</th>
                    <th>TITLE</th>
                    <th className="text-center">TYPE</th>
                    <th className="text-center">PRIORITY</th>
                    <th className="text-center">STORY POINTS</th>
                    <th className="text-center">ASSIGNEE</th>
                    <th className="text-center">STATUS</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleBacklogTickets.map((ticket) => (
                    <tr 
                      key={ticket.id}
                      draggable={isAuthorized}
                      onDragStart={(e) => handleDragStart(e, ticket)}
                      className={isAuthorized ? 'draggable-row' : ''}
                    >
                      {isAuthorized && (
                        <td>
                          <Checkbox 
                            size="small"
                            checked={selectedTicketIds.includes(ticket.id)}
                            onChange={() => handleSelectTicket(ticket.id)}
                            sx={LOCAL_CHECKBOX_SX}
                          />
                        </td>
                      )}
                      <td>
                        {ticket.code ? (
                          <a 
                            href={'/' + ENDPOINTS.TASK_URL(ticket.code)}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: COLORS.blue[600], fontWeight: 600, textDecoration: 'none' }}
                            onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                            onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                          >
                            {ticket.code}
                          </a>
                        ) : (
                          <span style={{ color: COLORS.blue[600], fontWeight: 600 }}>
                            {ticket.code || 'N/A'}
                          </span>
                        )}
                      </td>
                      <td className="title-cell">
                        <OverflowTooltip title={ticket.title} placement="top" component={Box}>
                          {ticket.title}
                        </OverflowTooltip>
                      </td>
                      <td className="text-center">
                        <span 
                          className="badge-pill"
                          style={{
                            backgroundColor: hexToRgba(getTaskTypeColor(ticket.type), 0.08),
                            color: getTaskTypeColor(ticket.type),
                            border: `1px solid ${hexToRgba(getTaskTypeColor(ticket.type), 0.18)}`
                          }}
                        >
                          {ticket.type}
                        </span>
                      </td>
                      <td className="text-center">
                        <span 
                          className="priority-badge"
                          style={{ color: getPriorityColor(ticket.priority) }}
                        >
                          {typeof ticket.priority === 'object' ? ticket.priority.name : ticket.priority}
                        </span>
                      </td>
                      <td className="text-center">
                        <span className="sp-box">{ticket.sp}</span>
                      </td>
                      <td className="text-center">
                        {(ticket.assignees && ticket.assignees.length > 0) || (Array.isArray(ticket.assignee) ? ticket.assignee.length > 0 : !!ticket.assignee) ? (
                          <Box display="inline-flex" justifyContent="center">
                            <AssigneeAvatarStack assignees={ticket.assignees} assignee={ticket.assignee} size={24} colorResolver={getAssigneeColor} onAvatarClick={handleAvatarClick} />
                          </Box>
                        ) : (
                          <Tooltip title="Unassigned" arrow placement="top" slotProps={{ tooltip: { className: 'app-tooltip' } }}>
                            <div className="backlog-assignee-avatar">
                              <PersonOutline sx={{ fontSize: 16 }} />
                            </div>
                          </Tooltip>
                        )}
                      </td>
                      <td className="text-center">
                        <span 
                          className="badge-pill"
                          style={{
                            backgroundColor: hexToRgba(getStatusColor(ticket.status), 0.08),
                            color: getStatusColor(ticket.status),
                            border: `1px solid ${hexToRgba(getStatusColor(ticket.status), 0.18)}`
                          }}
                        >
                          {ticket.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {isLoadingMore && (
              <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ color: COLORS.slate[500], fontWeight: 500, fontStyle: 'italic' }}>
                  Loading more tickets...
                </Typography>
              </Box>
            )}
          </div>
        </>
      ) : (
        <>
          {/* Sprint View Header */}
          <div className="backlog-table-header sprint-complete-backlog">
            <Box display="flex" alignItems="flex-start">
              {isAuthorized && currentMainSprint && !checkboxProps.hidden && (
                <Tooltip
                  title={checkboxProps.tooltip}
                  arrow
                  placement="top"
                  slotProps={{ tooltip: { className: 'app-tooltip' } }}
                >
                  <span>
                    <Radio 
                      size="small" 
                      checked={checkboxProps.checked} 
                      disabled={checkboxProps.disabled} 
                      onChange={checkboxProps.onChange}
                      disableRipple
                      sx={{
                        p: 0,
                        pr: '9px',
                        mt: 0.5,
                        mr: 1,
                        color: '#E4E4E4',
                        '&.Mui-checked': {
                          color: '#E4E4E4'
                        }
                      }}
                    />
                  </span>
                </Tooltip>
              )}
              <Box>
                <Typography variant="h6" fontWeight={600} color={COLORS.slate[900]} style={{ lineHeight: '1.25', display: 'flex', alignItems: 'center' }}>
                  {isLoading ? (
                    <Box className="skeleton-box" sx={{ width: 150, height: 28, borderRadius: 1 }} />
                  ) : (
                    currentMainSprint?.name || (selectedCardType === 'planned-sprint' ? t('backlog.noPlannedSprintTitle') : t('backlog.noSprintActiveTitle'))
                  )}
                  {currentMainSprint && (
                    <Tooltip
                      title={DRAG_HELP_MESSAGE}
                      arrow
                      placement="top"
                      slotProps={{ tooltip: { className: 'app-tooltip' } }}
                    >
                      <IconButton
                        size="small"
                        className="backlog-info-btn"
                        aria-label="Drag and drop help"
                        sx={{ ml: 1, p: 0.25, color: COLORS.slate[400], '&:hover': { color: COLORS.slate[600] } }}
                      >
                        <InfoOutlined sx={{ fontSize: 20 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Typography>
                <div className={`backlog-sprint-details ${isExpandedView ? 'expanded' : ''}`}>
                  {isLoading && !currentMainSprint ? (
                    <>
                      <Box className="skeleton-box" sx={{ width: 200, height: 16, mt: 0.5, borderRadius: 1 }} />
                      <Box className="skeleton-box" sx={{ width: 250, height: 16, mt: 0.5, borderRadius: 1 }} />
                    </>
                  ) : currentMainSprint ? (
                    <>
                      <Typography variant="caption" color="text.secondary" display="block" style={{ marginTop: '4px', fontSize: '0.8rem' }}>
                        {filteredMainSprintTickets.length} {filteredMainSprintTickets.length === 1 ? 'ticket' : 'tickets'} &bull; Goal: {currentMainSprint.goal || 'No goal set'} &bull; {formatDateRange(currentMainSprint.startDate, currentMainSprint.endDate)}
                      </Typography>
                    </>
                  ) : null}
                </div>
              </Box>
            </Box>

            <div className="backlog-table-controls">
              <Tooltip title={t('dashboard.createTask', 'Create Task')} arrow placement="top" slotProps={{ tooltip: { className: 'app-tooltip' } }}>
                <IconButton 
                  size="small" 
                  onClick={() => setIsAddTaskOpen(true)}
                  className="backlog-add-btn"
                  aria-label={t('dashboard.createTask', 'Create Task')}
                  sx={{ 
                    mr: 1, 
                    p: 0.5, 
                    color: COLORS.brand.accent, 
                    bgcolor: COLORS.blue[50],
                    border: `1px solid ${COLORS.blue[200] || '#bae6fd'}`,
                    borderRadius: '6px',
                    '&:hover': { 
                      color: COLORS.brand.accentHover,
                      bgcolor: COLORS.blue[100] || '#e0f2fe',
                      borderColor: COLORS.brand.accentHover
                    },
                    transition: 'all 0.2s ease-in-out'
                  }}
                >
                  <Add sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>

              <div className="backlog-search-wrapper">
                <Search className="search-icon" />
                <input 
                  type="text" 
                  placeholder="Search" 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <button 
                className={`backlog-sort-btn ${sortOption !== 'DEFAULT' ? 'active' : ''}`}
                onClick={handleSortClick}
                style={sortOption !== 'DEFAULT' ? { backgroundColor: COLORS.blue[50], color: COLORS.blue[600], borderColor: COLORS.blue[200] } : {}}
              >
                <SwapVert sx={{ fontSize: 18 }} className="sort-icon" />
                <span>
                  {sortOption === 'PRIORITY_HIGH_TO_LOW' ? 'Priority' : 
                   sortOption === 'SP_LOW_TO_HIGH' ? 'Story Points' : 'Sort'}
                </span>
              </button>

              <IconButton 
                size="small" 
                onClick={() => setIsExpandedView(!isExpandedView)}
                className="backlog-expand-btn"
              >
                {isExpandedView ? <FullscreenExit sx={{ fontSize: 18 }} /> : <Fullscreen sx={{ fontSize: 18 }} />}
              </IconButton>
            </div>
          </div>

          {/* Sprint tickets list */}
          <div 
            className="infinite-scroll-container"
            onScroll={(e) => handleScroll(e, 'sprint')}
          >
            {isLoading ? (
              <Box sx={{ p: 3 }}>
                {[1, 2, 3].map(i => (
                  <Box key={i} className="skeleton-box" sx={{ height: 48, mb: 1, width: '100%' }} />
                ))}
              </Box>
            ) : filteredMainSprintTickets.length === 0 ? (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center', 
                p: 6, 
                border: isDragOverSprint ? `2px dashed ${COLORS.brand.primary}` : '2px dashed transparent', 
                borderRadius: 2,
                bgcolor: isDragOverSprint ? 'rgba(37, 99, 235, 0.02)' : 'transparent',
                minHeight: '300px',
                height: '100%',
                flex: 1,
                transition: 'all 0.2s ease-in-out'
              }}>
                <AssignmentOutlined sx={{ fontSize: 54, color: isDragOverSprint ? COLORS.brand.primary : COLORS.slate[300], mb: 2 }} />
                <Typography variant="h6" color={isDragOverSprint ? COLORS.brand.primary : COLORS.slate[700]} fontWeight={600} gutterBottom>
                  {isMyTicketsDataView ? t('backlog.noTicketsAssigned') : (selectedCardType === 'planned-sprint' && !currentMainSprint ? t('backlog.noPlannedSprintEmpty') : t('backlog.thisSprintIsEmpty'))}
                </Typography>
                <Typography variant="body2" color={COLORS.slate[500]} align="center" maxWidth={350}>
                  {isMyTicketsDataView 
                    ? t('backlog.noTicketsAssignedDesc') 
                    : (selectedCardType === 'planned-sprint' && !currentMainSprint ? t('backlog.noPlannedSprintDesc') : t('backlog.thisSprintIsEmptyDesc'))}
                </Typography>
              </Box>
            ) : (
              <table className="backlog-table">
                <thead>
                  <tr>
                    {isAuthorized && (
                      <th>
                        <Checkbox 
                          size="small"
                          checked={visibleSprintTickets.length > 0 && visibleSprintTickets.every(t => selectedTicketIds.includes(t.id))}
                          indeterminate={visibleSprintTickets.some(t => selectedTicketIds.includes(t.id)) && !visibleSprintTickets.every(t => selectedTicketIds.includes(t.id))}
                          onChange={() => handleSelectAllVisible(visibleSprintTickets)}
                          sx={LOCAL_CHECKBOX_SX}
                        />
                      </th>
                    )}
                    <th>TICKET</th>
                    <th>TITLE</th>
                    <th className="text-center">TYPE</th>
                    <th className="text-center">PRIORITY</th>
                    <th className="text-center">STORY POINTS</th>
                    <th className="text-center">ASSIGNEE</th>
                    <th className="text-center">STATUS</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleSprintTickets.map((ticket) => (
                    <tr 
                      key={ticket.id}
                      draggable={isAuthorized}
                      onDragStart={(e) => handleDragStart(e, ticket)}
                      className={isAuthorized ? 'draggable-row' : ''}
                    >
                      {isAuthorized && (
                        <td>
                          <Checkbox 
                            size="small"
                            checked={selectedTicketIds.includes(ticket.id)}
                            onChange={() => handleSelectTicket(ticket.id)}
                            sx={LOCAL_CHECKBOX_SX}
                          />
                        </td>
                      )}
                      <td>
                        {ticket.code ? (
                          <a 
                            href={'/' + ENDPOINTS.TASK_URL(ticket.code)}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: COLORS.blue[600], fontWeight: 600, textDecoration: 'none' }}
                            onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                            onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                          >
                            {ticket.code}
                          </a>
                        ) : (
                          <span style={{ color: COLORS.blue[600], fontWeight: 600 }}>
                            {ticket.code || 'N/A'}
                          </span>
                        )}
                      </td>
                      <td className="title-cell">
                        <OverflowTooltip title={ticket.title} placement="top" component={Box}>
                          {ticket.title}
                        </OverflowTooltip>
                      </td>
                      <td className="text-center">
                        <span 
                          className="badge-pill"
                          style={{
                            backgroundColor: hexToRgba(getTaskTypeColor(ticket.type), 0.08),
                            color: getTaskTypeColor(ticket.type),
                            border: `1px solid ${hexToRgba(getTaskTypeColor(ticket.type), 0.18)}`
                          }}
                        >
                          {ticket.type}
                        </span>
                      </td>
                      <td className="text-center">
                        <span 
                          className="priority-badge"
                          style={{ color: getPriorityColor(ticket.priority) }}
                        >
                          {typeof ticket.priority === 'object' ? ticket.priority.name : ticket.priority}
                        </span>
                      </td>
                      <td className="text-center">
                        <span className="sp-box">{ticket.sp}</span>
                      </td>
                      <td className="text-center">
                        {(ticket.assignees && ticket.assignees.length > 0) || (Array.isArray(ticket.assignee) ? ticket.assignee.length > 0 : !!ticket.assignee) ? (
                          <Box display="inline-flex" justifyContent="center">
                            <AssigneeAvatarStack assignees={ticket.assignees} assignee={ticket.assignee} size={24} colorResolver={getAssigneeColor} onAvatarClick={handleAvatarClick} />
                          </Box>
                        ) : (
                          <Tooltip title="Unassigned" arrow placement="top" slotProps={{ tooltip: { className: 'app-tooltip' } }}>
                            <div className="backlog-assignee-avatar">
                              <PersonOutline sx={{ fontSize: 16 }} />
                            </div>
                          </Tooltip>
                        )}
                      </td>
                      <td className="text-center">
                        <span 
                          className="badge-pill"
                          style={{
                            backgroundColor: hexToRgba(getStatusColor(ticket.status), 0.08),
                            color: getStatusColor(ticket.status),
                            border: `1px solid ${hexToRgba(getStatusColor(ticket.status), 0.18)}`
                          }}
                        >
                          {ticket.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {isLoadingMore && (
              <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ color: COLORS.slate[500], fontWeight: 500, fontStyle: 'italic' }}>
                  Loading more tickets...
                </Typography>
              </Box>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default BacklogTable;
